import { createPortal } from 'react-dom'

import useBoardTabBarPortal from '../hooks/useBoardTabBarPortal'
import boardStyles from '../../ProjectStoryMapBoard.module.css'
import styles from './StoryMapBoardSkeleton.module.css'

const STEP_COUNT = 5
const JOURNEY_SPANS = [3, 2]

const buildGridTemplateColumns = (stepCount) =>
    Array.from({ length: stepCount }, (_, index) => {
        const step = 'var(--sb-step-width, 200px)'
        const gap = 'var(--sb-insert-width, 28px)'
        return index < stepCount - 1 ? `${step} ${gap}` : step
    }).join(' ')

const getJourneySpan = (stepCount) =>
    stepCount > 1 ? stepCount * 2 - 1 : 1

const STORY_COUNTS_BY_RELEASE = [
    [2, 1, 2, 1, 1],
    [1, 0, 1, 0, 0],
]

const StoryCardSkeleton = () => (
    <div className={styles.skStoryCard}>
        <div className={styles.skStoryCardInner}>
            <div className={`${styles.skBlock} ${styles.skStoryStatus}`} />
            <div className={`${styles.skBlock} ${styles.skStoryName}`} />
        </div>
    </div>
)

const ReleaseSkeleton = ({ storyCounts, gridTemplateColumns }) => (
    <div className={styles.skReleaseBlock}>
        <div className={styles.skReleaseHeader}>
            <div className={styles.skReleaseLeft}>
                <div className={`${styles.skBlock} ${styles.skReleaseIcon}`} />
                <div className={`${styles.skBlock} ${styles.skReleaseTitle}`} />
                <div className={`${styles.skBlock} ${styles.skReleaseMeta}`} />
            </div>
            <div className={styles.skReleaseRight}>
                <div className={`${styles.skBlock} ${styles.skReleaseDate}`} />
                <div className={`${styles.skBlock} ${styles.skReleaseDate}`} />
            </div>
        </div>

        <div
            className={styles.skStoryGrid}
            style={{ gridTemplateColumns }}
        >
            {storyCounts.map((count, columnIndex) => (
                <div
                    key={columnIndex}
                    className={styles.skStoryColumn}
                    style={{ gridColumn: columnIndex * 2 + 1 }}
                >
                    {Array.from({ length: count }, (_, storyIndex) => (
                        <StoryCardSkeleton key={storyIndex} />
                    ))}
                </div>
            ))}
        </div>
    </div>
)

const StoryMapBoardSkeleton = () => {
    const { tabBarSlot, useInlineHeader } = useBoardTabBarPortal()
    const gridTemplateColumns = buildGridTemplateColumns(STEP_COUNT)

    let journeyGridColumn = 1
    const journeyGroups = JOURNEY_SPANS.map((stepCount, index) => {
        const span = getJourneySpan(stepCount)
        const group = {
            key: `journey-${index}`,
            gridColumn: `${journeyGridColumn} / span ${span}`,
            accentTone: index % 2 === 0 ? '#c7d2fe' : '#ddd6fe',
        }
        journeyGridColumn += span + 1
        return group
    })

    const header = (
        <div
            className={`${boardStyles.boardHeader} ${
                tabBarSlot ? boardStyles.boardHeaderTabBar : ''
            }`}
        >
            <div className={`${styles.skBlock} ${styles.skBackBtn}`} aria-hidden />
            <div className={boardStyles.boardHeaderContent}>
                <div className={`${styles.skBlock} ${styles.skTitle}`} aria-hidden />
                <span className={boardStyles.boardHeaderDivider} aria-hidden="true" />
                <div className={styles.skStats} aria-hidden>
                    {Array.from({ length: 4 }, (_, index) => (
                        <div key={index} className={`${styles.skBlock} ${styles.skStat}`} />
                    ))}
                </div>
            </div>
        </div>
    )

    return (
        <div
            className={boardStyles.boardPage}
            aria-busy="true"
            aria-label="Loading story map"
        >
            {tabBarSlot ? createPortal(header, tabBarSlot) : null}
            {useInlineHeader ? header : null}

            <div className={boardStyles.boardBody}>
                <div className={boardStyles.mapCanvas}>
                    <div className={boardStyles.mapTopPane}>
                        <div className={styles.skTopSection}>
                            <div
                                className={styles.skJourneyRow}
                                style={{ gridTemplateColumns }}
                            >
                                {journeyGroups.map((group) => (
                                    <div
                                        key={group.key}
                                        className={styles.skJourneyGroup}
                                        style={{ gridColumn: group.gridColumn }}
                                    >
                                        <div
                                            className={`${styles.skBlock} ${styles.skJourneyAccent}`}
                                            style={{ backgroundColor: group.accentTone }}
                                        />
                                        <div
                                            className={`${styles.skBlock} ${styles.skJourneyLabel}`}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div
                                className={styles.skStepRow}
                                style={{ gridTemplateColumns }}
                            >
                                {Array.from({ length: STEP_COUNT }, (_, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            gridColumn: index * 2 + 1,
                                        }}
                                    >
                                        <div className={styles.skStepCard}>
                                            <div className={styles.skStepCardInner}>
                                                <div
                                                    className={`${styles.skBlock} ${styles.skStepMeta}`}
                                                />
                                                <div
                                                    className={`${styles.skBlock} ${styles.skStepName}`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={boardStyles.mapBodyPane}>
                        {STORY_COUNTS_BY_RELEASE.map((storyCounts, index) => (
                            <ReleaseSkeleton
                                key={index}
                                storyCounts={storyCounts}
                                gridTemplateColumns={gridTemplateColumns}
                            />
                        ))}
                        <div className={`${styles.skBlock} ${styles.skAddRelease}`} aria-hidden />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StoryMapBoardSkeleton
